import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { showSuccess, showError } from '../../utils/toast';
import { CheckCircle2, ShieldCheck, ShieldAlert, Sparkles, Sliders } from 'lucide-react';

const AutoApprovalSettingCard = ({ onStatusChange, compact = false }) => {
  const [autoApproval, setAutoApproval] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchSetting();
  }, []);

  const fetchSetting = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/settings/auto-approval');
      if (res.data && res.data.success) {
        setAutoApproval(Boolean(res.data.autoApproval));
        if (onStatusChange) onStatusChange(Boolean(res.data.autoApproval));
      }
    } catch (err) {
      console.error('Error fetching auto approval setting:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleModeChange = async (isAuto) => {
    if (updating || isAuto === autoApproval) return;
    try {
      setUpdating(true);
      const res = await api.post('/admin/settings/auto-approval', { autoApproval: isAuto });
      if (res.data && res.data.success) {
        setAutoApproval(isAuto);
        showSuccess(res.data.message || (isAuto ? 'Auto-Approval ON' : 'Manual Approval ON'));
        if (onStatusChange) onStatusChange(isAuto);
      } else {
        showError('सेटिंग सेव करने में त्रुटि हुई');
      }
    } catch (err) {
      console.error('Error saving auto approval setting:', err);
      showError(err.response?.data?.message || 'सेटिंग सेव नहीं हो सकी');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-2xl p-5 border border-gray-100 dark:border-gray-800 animate-pulse">
        <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-1/3 mb-3"></div>
        <div className="h-10 bg-gray-100 dark:bg-gray-800/60 rounded-xl"></div>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-900 rounded-2xl border transition-all duration-200 shadow-xs ${
      autoApproval 
        ? 'border-emerald-200 dark:border-emerald-900/60 bg-gradient-to-br from-emerald-50/30 to-white dark:from-emerald-950/10 dark:to-gray-900' 
        : 'border-amber-200 dark:border-amber-900/60 bg-gradient-to-br from-amber-50/30 to-white dark:from-amber-950/10 dark:to-gray-900'
    } ${compact ? 'p-4' : 'p-5 sm:p-6'}`}>
      
      {/* Header Row */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
            autoApproval 
              ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300' 
              : 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-300'
          }`}>
            {autoApproval ? <ShieldCheck className="w-5 h-5" /> : <ShieldAlert className="w-5 h-5" />}
          </div>
          <div>
            <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <span>लिस्टिंग मंज़ूरी सेटिंग (Approval Mode)</span>
              {updating && (
                <span className="text-xs text-primary-500 font-normal animate-pulse">सेव हो रहा है...</span>
              )}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              बिजनेस, भावी सरपंच व अन्य लिस्टिंग सबमिट होने पर मंज़ूरी की व्यवस्था चुनें।
            </p>
          </div>
        </div>

        {/* Live Status Badge */}
        <div className="self-start sm:self-auto">
          {autoApproval ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 dark:bg-emerald-950/80 text-emerald-800 dark:text-emerald-200 border border-emerald-300 dark:border-emerald-800 shadow-2xs">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              Auto-Approval: चालू (ON)
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-100 dark:bg-amber-950/80 text-amber-800 dark:text-amber-200 border border-amber-300 dark:border-amber-800 shadow-2xs">
              <span className="w-2 h-2 rounded-full bg-amber-500"></span>
              Manual Mode: बंद (OFF)
            </span>
          )}
        </div>
      </div>

      {/* Radio Options Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
        
        {/* Radio Option 1: Auto-Approval ON */}
        <label
          onClick={() => handleModeChange(true)}
          className={`flex items-start gap-3.5 p-3.5 sm:p-4 rounded-xl border-2 cursor-pointer transition-all ${
            autoApproval
              ? 'border-emerald-500 bg-emerald-50/80 dark:bg-emerald-950/30 dark:border-emerald-500 shadow-sm'
              : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 bg-white dark:bg-gray-850'
          }`}
        >
          <div className="pt-0.5">
            <input
              type="radio"
              name="approvalMode"
              checked={autoApproval === true}
              onChange={() => handleModeChange(true)}
              disabled={updating}
              className="w-4 h-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 dark:border-gray-600 cursor-pointer"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm sm:text-base text-gray-900 dark:text-white">
                Auto-Approval ON (स्वचालित मंज़ूरी)
              </span>
              <span className="px-2 py-0.5 rounded text-[10px] font-extrabold uppercase bg-emerald-500 text-white tracking-wide">
                Instant Live
              </span>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 leading-relaxed">
              कोई भी नई लिस्टिंग (बिजनेस, भावी सरपंच प्रत्याशी आदि) सबमिट होते ही <strong>तुरंत लाइव / अप्रूव</strong> हो जाएगी। (रिव्यु बिना अप्रूवल सीधे पब्लिश होते हैं)
            </p>
          </div>
        </label>

        {/* Radio Option 2: Manual Approval OFF */}
        <label
          onClick={() => handleModeChange(false)}
          className={`flex items-start gap-3.5 p-3.5 sm:p-4 rounded-xl border-2 cursor-pointer transition-all ${
            !autoApproval
              ? 'border-amber-500 bg-amber-50/80 dark:bg-amber-950/30 dark:border-amber-500 shadow-sm'
              : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 bg-white dark:bg-gray-850'
          }`}
        >
          <div className="pt-0.5">
            <input
              type="radio"
              name="approvalMode"
              checked={autoApproval === false}
              onChange={() => handleModeChange(false)}
              disabled={updating}
              className="w-4 h-4 text-amber-600 focus:ring-amber-500 border-gray-300 dark:border-gray-600 cursor-pointer"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm sm:text-base text-gray-900 dark:text-white">
                Manual Approval OFF (मैन्युअल मंज़ूरी)
              </span>
              <span className="px-2 py-0.5 rounded text-[10px] font-extrabold uppercase bg-amber-500 text-white tracking-wide">
                Review Required
              </span>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 leading-relaxed">
              नई लिस्टिंग सबमिट होने पर <strong>पेंडिंग (Pending Requests)</strong> में जाएगी। जब एडमिन उसे चेक करके Approve करेगा तभी वह साइट पर लाइव होगी।
            </p>
          </div>
        </label>

      </div>
    </div>
  );
};

export default AutoApprovalSettingCard;
